import React, { useState, useEffect, useMemo } from "react";
import { Modal, Select, Input, Switch } from "antd";
import { notify } from "../../../../services/toast.config";
import { REFERRAL_RELATIONSHIPS } from "../../constants/recruitment.constants";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { submitReferral } from "../../../../core/data/redux/recruitment/thunks";
import type { Job } from "../../../../core/data/redux/recruitment/types";
import { selectDepartments } from "../../../../core/data/redux/dashboardSlice";

const { TextArea } = Input;

interface ReferralFormProps {
  show: boolean;
  onClose: () => void;
  jobs: Job[];
  onSubmitted: () => void;
}

const EMPTY_FORM = {
  candidate_name: "",
  candidate_email: "",
  candidate_phone: "",
  job_id: null as number | string | null,
  relationship: "",
  why_good_fit: "",
};



const Label: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
  <label
    style={{
      display: "block",
      fontSize: 11,
      fontWeight: 700,
      marginBottom: 5,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }}
  >
    {text}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
);

const Divider = () => (
  <div style={{ height: 1, background: "#f1f5f9", margin: "2px 0" }} />
);



const ReferralForm: React.FC<ReferralFormProps> = ({
  show,
  onClose,
  jobs,
  onSubmitted,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  
  useEffect(() => {
    if (show) setForm(EMPTY_FORM);
  }, [show]);

  const departments = useSelector(selectDepartments);
  const deptMap = useMemo(() => {
    const map: Record<number, string> = {};
    (departments || []).forEach((d: any) => { map[d.id] = d.name; });
    return map;
  }, [departments]);

  const jobOptions = jobs.map((j) => ({
    value: j.id,
    label: j.job_title
      ? `${j.job_title}${(j as any).department ? ` — ${(j as any).department}` : deptMap[(j as any).department_id] ? ` — ${deptMap[(j as any).department_id]}` : ""}`
      : String(j.id),
  }));

  const set = (field: string, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleClose = () => {
    setForm(EMPTY_FORM);
    onClose();
  };

  const isFilled =
    form.candidate_name.trim() &&
    form.candidate_email.trim() &&
    form.candidate_phone.trim() &&
    form.job_id != null &&
    form.relationship &&
    form.why_good_fit.trim();

  const handleSubmit = async () => {
    if (!isFilled) {
      notify.warn("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(
        submitReferral({
          candidate_name: form.candidate_name,
          candidate_email: form.candidate_email,
          candidate_phone: form.candidate_phone,
          job_id: Number(form.job_id),
          relationship: form.relationship,
          why_good_fit: form.why_good_fit,
        }),
      ).unwrap();
      notify.success("Referral submitted successfully!");
      onSubmitted();
      handleClose();
    } catch (err: any) {
      notify.error(
        typeof err === "string" ? err : err?.message || "Failed to submit referral",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={show}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              flexShrink: 0,
              background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
              border: "1px solid #c7d2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="ti ti-user-plus" style={{ fontSize: 17, color: "#4f46e5" }} />
          </div>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.25,
              }}
            >
              Refer a Candidate
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400, marginTop: 2 }}>
              Help us find great talent from your network
            </div>
          </div>
        </div>
      }
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <i className="ti ti-send" style={{ fontSize: 13 }} />
          Submit Referral
        </span>
      }
      okButtonProps={{
        disabled: !isFilled,
        loading: submitting,
        style: {
          background: isFilled ? "#4f46e5" : undefined,
          borderColor: isFilled ? "#4f46e5" : undefined,
          borderRadius: 8,
          fontWeight: 600,
          height: 36,
          paddingInline: 20,
        },
      }}
      cancelButtonProps={{
        disabled: submitting,
        style: { borderRadius: 8, height: 36 },
      }}
      width={800}
      destroyOnClose
      zIndex={1111}
      style={{ top: 40 }}
      styles={{
        mask: {
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(15, 23, 42, 0.4)",
        },
        header: {
          padding: "16px 22px 14px",
          borderBottom: "1px solid #f1f5f9",
          marginBottom: 0,
        },
        body: {
          padding: "18px 22px",
          maxHeight: "calc(100vh - 80px - 70px - 64px)",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "#e2e8f0 transparent",
        },
        footer: {
          padding: "12px 22px",
          borderTop: "1px solid #f1f5f9",
          marginTop: 0,
        },
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

        
        <div
          style={{
            padding: "14px 16px",
            background: "#f8fafc",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#4f46e5",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <i className="ti ti-user" style={{ fontSize: 13 }} />
            Candidate Information
          </div>

          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <Label text="Full Name" required />
              <Input
                prefix={
                  <i
                    className="ti ti-user"
                    style={{ fontSize: 12, color: "#a5b4fc" }}
                  />
                }
                placeholder="e.g. Ravi Kumar"
                value={form.candidate_name}
                onChange={(e) => set("candidate_name", e.target.value)}
              />
            </div>
            <div>
              <Label text="Email" required />
              <Input
                type="email"
                prefix={
                  <i
                    className="ti ti-mail"
                    style={{ fontSize: 12, color: "#a5b4fc" }}
                  />
                }
                placeholder="candidate@email.com"
                value={form.candidate_email}
                onChange={(e) => set("candidate_email", e.target.value)}
              />
            </div>
          </div>

          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <Label text="Phone" required />
              <Input
                type="tel"
                prefix={
                  <i
                    className="ti ti-phone"
                    style={{ fontSize: 12, color: "#a5b4fc" }}
                  />
                }
                placeholder="+91 98765 43210"
                value={form.candidate_phone}
                onChange={(e) => set("candidate_phone", e.target.value)}
              />
            </div>
            <div>
              <Label text="Job Position" required />
              <Select
                style={{ width: "100%" }}
                placeholder={
                  jobs.length === 0
                    ? "Loading positions…"
                    : "Select a position…"
                }
                loading={jobs.length === 0}
                value={form.job_id != null ? form.job_id : undefined}
                onChange={(val) => set("job_id", val ?? null)}
                options={jobOptions}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                notFoundContent={
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px 0",
                      color: "#94a3b8",
                      fontSize: 13,
                    }}
                  >
                    <i
                      className="ti ti-briefcase-off d-block mb-1"
                      style={{ fontSize: 24, opacity: 0.4 }}
                    />
                    No active positions found
                  </div>
                }
              />
            </div>
          </div>
        </div>

        <Divider />

        
        <div>
          <Label text="Your Relationship with the Candidate" required />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 2 }}>
            {REFERRAL_RELATIONSHIPS.map((rel) => {
              const active = form.relationship === rel.value;
              return (
                <button
                  key={rel.value}
                  type="button"
                  onClick={() => set("relationship", rel.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 14px",
                    borderRadius: 8,
                    cursor: "pointer",
                    border: `1.5px solid ${active ? "#4f46e5" : "#e2e8f0"}`,
                    background: active ? "#eef2ff" : "#fff",
                    color: active ? "#4f46e5" : "#64748b",
                    fontSize: 12,
                    fontWeight: active ? 700 : 500,
                    transition: "all 0.15s",
                  }}
                >
                  {active && (
                    <i
                      className="ti ti-check"
                      style={{ fontSize: 12, color: "#4f46e5" }}
                    />
                  )}
                  {rel.label}
                </button>
              );
            })}
          </div>
        </div>

        <Divider />

        
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 6,
            }}
          >
            <Label text="Why do you recommend them?" required />
            {form.why_good_fit && (
              <button
                type="button"
                onClick={() => set("why_good_fit", "")}
                style={{
                  fontSize: 11,
                  color: "#94a3b8",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i className="ti ti-x" style={{ fontSize: 11 }} />
                Clear
              </button>
            )}
          </div>
          <TextArea
            rows={4}
            placeholder="Briefly explain why this person would be a great fit for the role. Mention relevant skills, experiences, or qualities…"
            value={form.why_good_fit}
            onChange={(e) => set("why_good_fit", e.target.value)}
            maxLength={500}
            showCount
            style={{ fontSize: 13, resize: "none", borderRadius: 8 }}
          />
        </div>

        
        {!isFilled && (form.candidate_name || form.candidate_email) && (
          <div
            style={{
              fontSize: 12,
              color: "#92400e",
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 7,
              padding: "7px 11px",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i
              className="ti ti-alert-triangle"
              style={{ fontSize: 13, flexShrink: 0 }}
            />
            Please complete all required fields before submitting.
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReferralForm;